import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer, Box, Typography, IconButton, Button, Stack, Chip,
  CircularProgress, Divider, TextField, Select, MenuItem,
  FormControl, InputLabel, FormHelperText,
} from '@mui/material';
import {
  Close, AssignmentReturn, CheckCircle, Warning,
  Add, Remove, AccessTime, Print,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  useGetReturnReasonCodesQuery,
  useCreateReturnMutation,
  useCompleteReturnMutation,
} from '../../services/returnApi';
import { customerApi } from '../../services/customerApi';
import { orderApi } from '../../services/orderApi';
import { useToast } from '../common/ToastProvider';
import { usePrinterContext, browserPrintReturn } from '../../context/PrinterContext';
import { mapReturnToReceipt } from './receipt/mapReturnToReceipt';
import WhatsAppSection from './checkout/WhatsAppBillSection';
import ItemBarcode from './ItemBarcode';
import { useGetReceiptTemplateQuery } from '../../services/receiptTemplateApi';

// ── Step indicator ──────────────────────────────────────────────────────────

function StepIndicator({ step }) {
  const steps = [
    { key: 'select', label: 'Select' },
    { key: 'review', label: 'Review' },
    { key: 'success', label: 'Done' },
  ];
  const idx = steps.findIndex((s) => s.key === step);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
      {steps.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <React.Fragment key={s.key}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
              <Box sx={{
                width: 22, height: 22, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 800,
                bgcolor: done ? '#16A34A' : active ? '#C2410C' : 'transparent',
                color: done || active ? '#fff' : '#94A3B8',
                border: done || active ? 'none' : '2px solid #CBD5E1',
                transition: 'all 0.2s',
              }}>
                {done ? '✓' : i + 1}
              </Box>
              <Typography
                fontSize="0.6rem"
                fontWeight={active ? 700 : 500}
                color={active ? '#C2410C' : done ? '#16A34A' : '#94A3B8'}
                sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1 }}
              >
                {s.label}
              </Typography>
            </Box>
            {i < steps.length - 1 && (
              <Box sx={{ width: 20, height: 2, bgcolor: i < idx ? '#16A34A' : '#E2E8F0', mx: 0.5, flexShrink: 0 }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// ── Refund method label helper ──────────────────────────────────────────────

const REFUND_METHOD_LABELS = {
  cash: 'Cash',
  store_credit: 'Store Credit',
  same_payment_mode: 'Same Payment Mode',
};

const REFUND_METHOD_COLORS = {
  cash: { bgcolor: '#EFF6FF', color: '#2563EB' },
  store_credit: { bgcolor: '#F5F3FF', color: '#7C3AED' },
  same_payment_mode: { bgcolor: '#EEF2FF', color: '#4F46E5' },
};

function RefundMethodChip({ method }) {
  const cfg = REFUND_METHOD_COLORS[method] || { bgcolor: '#F1F5F9', color: '#64748B' };
  return (
    <Chip
      label={REFUND_METHOD_LABELS[method] || method}
      size="small"
      sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, ...cfg }}
    />
  );
}

// ── Main component ──────────────────────────────────────────────────────────

const ReturnItemsDrawer = ({ open, onClose, order }) => {
  const showToast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isConnected, silentPrintReturn } = usePrinterContext();
  const { data: receiptTemplate } = useGetReceiptTemplateQuery();
  const organization = useSelector((s) => s.auth.organization);

  const returnRefundPolicy = organization?.return_refund_policy || 'cash';
  const reasonRequired = organization?.return_reason_required || false;
  const returnWindowDays = organization?.return_window_days || null;
  const cashierDecides = returnRefundPolicy === 'cashier_decides';
  const hasCustomer = !!(order?.customer_name || order?.customer?.name);
  const orderCustomerId = order?.customer_id || order?.customer?.id || null;

  // ── Steps ────────────────────────────────────────────────────────────────
  const [step, setStep] = useState('select');
  const [refundMethod, setRefundMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  // itemSelections: { [order_item_id]: { qty, reasonCodeId } }
  const [itemSelections, setItemSelections] = useState({});
  const [itemErrors, setItemErrors] = useState({});
  const [draftReturn, setDraftReturn] = useState(null);
  const [completedReturn, setCompletedReturn] = useState(null);

  const { data: codesData } = useGetReturnReasonCodesQuery(undefined, { skip: !open });
  const reasonCodes = codesData?.data?.return_reason_codes || [];

  const [createReturn, { isLoading: isCreating }] = useCreateReturnMutation();
  const [completeReturn, { isLoading: isCompleting }] = useCompleteReturnMutation();

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep('select');
      setRefundMethod('cash');
      setNotes('');
      setItemSelections({});
      setItemErrors({});
      setDraftReturn(null);
      setCompletedReturn(null);
    }
  }, [open]);

  // ── Return window check ──────────────────────────────────────────────────
  const windowExpired = (() => {
    if (!returnWindowDays || !order?.paid_at) return false;
    const daysSince = Math.floor((Date.now() - new Date(order.paid_at)) / 86400000);
    return daysSince > returnWindowDays;
  })();

  const daysRemaining = (() => {
    if (!returnWindowDays || !order?.paid_at) return null;
    const daysSince = Math.floor((Date.now() - new Date(order.paid_at)) / 86400000);
    return returnWindowDays - daysSince;
  })();

  const paidDate = order?.paid_at
    ? new Date(order.paid_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  // ── Items ────────────────────────────────────────────────────────────────
  const orderItems = order?.items || [];

  const getSelection = (itemId) => itemSelections[itemId] || { qty: 0, reasonCodeId: '' };

  const setQty = (itemId, qty) => {
    const item = orderItems.find((i) => i.id === itemId);
    if (!item) return;
    const max = parseFloat(item.quantity_returnable ?? item.quantity);
    const clamped = Math.max(0, Math.min(qty, max));
    setItemSelections((prev) => ({ ...prev, [itemId]: { ...getSelection(itemId), qty: clamped } }));
    if (itemErrors[itemId]) setItemErrors((prev) => { const e = { ...prev }; delete e[itemId]; return e; });
  };

  const setReasonCode = (itemId, code) => {
    setItemSelections((prev) => ({ ...prev, [itemId]: { ...getSelection(itemId), reasonCodeId: code } }));
  };

  const selectedItems = orderItems.filter((i) => getSelection(i.id).qty > 0);
  const selectedCount = selectedItems.length;

  // ── Validate ─────────────────────────────────────────────────────────────
  const validate = () => {
    if (selectedCount === 0) return false;
    if (!reasonRequired) return true;
    const errors = {};
    selectedItems.forEach((item) => {
      if (!getSelection(item.id).reasonCodeId) {
        errors[item.id] = 'Reason required';
      }
    });
    if (Object.keys(errors).length > 0) {
      setItemErrors(errors);
      return false;
    }
    return true;
  };

  // ── Create draft ──────────────────────────────────────────────────────────
  const handleNextReview = async () => {
    // Draft already exists (user went back from review) — reuse it, no new API call
    if (draftReturn) {
      setStep('review');
      return;
    }
    if (!validate()) return;
    try {
      const items = selectedItems.map((item) => {
        const sel = getSelection(item.id);
        const obj = { order_item_id: item.id, quantity_returned: sel.qty };
        if (sel.reasonCodeId) obj.return_reason_code_id = sel.reasonCodeId;
        return obj;
      });
      const payload = {
        orderId: order.id,
        items,
        ...(notes ? { notes } : {}),
        ...(cashierDecides ? { refund_method: refundMethod } : {}),
      };
      const res = await createReturn(payload).unwrap();
      setDraftReturn(res.data.order_return);
      setStep('review');
    } catch (err) {
      const msg = err?.data?.error || err?.data?.errors?.join(', ') || 'Failed to create return';
      showToast(msg, 'error');
    }
  };

  // ── Complete return ───────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!draftReturn) return;
    try {
      const res = await completeReturn({ orderId: order.id, id: draftReturn.id }).unwrap();
      const completed = res?.data?.order_return;
      const completedMethod = completed?.refund_method || resolvedMethod;
      if (completedMethod === 'store_credit') {
        const tags = [{ type: 'Customer', id: 'LIST' }];
        if (orderCustomerId) {
          tags.push({ type: 'Customer', id: orderCustomerId });
        }
        dispatch(customerApi.util.invalidateTags(tags));
      }
      // Refetch the order so quantity_returnable updates immediately without a page refresh
      dispatch(orderApi.util.invalidateTags([{ type: 'Order', id: order.id }]));
      setCompletedReturn(res.data.order_return);
      setStep('success');
    } catch (err) {
      // Promotion clawback drifted since the draft was quoted (another
      // return/exchange completed in between). The backend refreshed the
      // draft with new figures — show them and let the cashier re-confirm.
      if (err?.data?.details?.code === 'clawback_recalculated' && err?.data?.details?.order_return) {
        setDraftReturn(err.data.details.order_return);
        showToast('Refund amount was recalculated — please review and confirm again', 'warning');
        return;
      }
      const msg = err?.data?.error || err?.data?.errors?.join(', ') || 'Failed to complete return';
      showToast(msg, 'error');
    }
  };

// ── Close ─────────────────────────────────────────────────────────────────
  const handleClose = () => {
    if (isCreating || isCompleting) return;
    onClose();
  };

  // ── Resolved refund method for display ────────────────────────────────────
  const resolvedMethod = cashierDecides ? refundMethod : returnRefundPolicy;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 480 },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 2,
        borderBottom: '1px solid #E2E8F0',
        display: 'flex', alignItems: 'center', gap: 1.5,
        flexShrink: 0, bgcolor: '#fff',
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px',
          bgcolor: '#FFF7ED', display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <AssignmentReturn sx={{ color: '#C2410C', fontSize: 20 }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography fontWeight={800} fontSize="1rem" color="#1E293B">
            Return Items
          </Typography>
          {order && (
            <Typography fontSize="0.68rem" color="#94A3B8" noWrap>
              Order {order.order_number}
            </Typography>
          )}
        </Box>
        <StepIndicator step={step} />
        <IconButton size="small" onClick={handleClose} sx={{ color: '#64748B', ml: 0.5 }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{
        flexGrow: 1, overflowY: 'auto', p: 2,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
      }}>
        {step === 'select' && (
          <SelectStep
            order={order}
            orderItems={orderItems}
            windowExpired={windowExpired}
            daysRemaining={daysRemaining}
            paidDate={paidDate}
            returnWindowDays={returnWindowDays}
            cashierDecides={cashierDecides}
            refundMethod={refundMethod}
            setRefundMethod={setRefundMethod}
            resolvedMethod={resolvedMethod}
            hasCustomer={hasCustomer}
            notes={notes}
            setNotes={setNotes}
            reasonCodes={reasonCodes}
            reasonRequired={reasonRequired}
            selectedCount={selectedCount}
            getSelection={getSelection}
            setQty={setQty}
            setReasonCode={setReasonCode}
            itemErrors={itemErrors}
          />
        )}
        {step === 'review' && draftReturn && (
          <ReviewStep
            draftReturn={draftReturn}
            resolvedMethod={resolvedMethod}
            notes={notes}
          />
        )}
        {step === 'success' && completedReturn && (
          <SuccessStep
            completedReturn={completedReturn}
            receipt={mapReturnToReceipt(completedReturn, receiptTemplate)}
            onPrint={async () => {
              const receipt = mapReturnToReceipt(completedReturn, receiptTemplate);
              if (!receipt) return;
              if (isConnected) {
                try { await silentPrintReturn(receipt); return; } catch (err) {
                  console.error('[Printer] silentPrintReturn failed:', err);
                  showToast('Printer error — switching to browser print', 'warning');
                }
              } else {
                showToast('Printer app not connected — printing via browser', 'info');
              }
              await new Promise((r) => setTimeout(r, 1500));
              browserPrintReturn(receipt);
            }}
          />
        )}
      </Box>

      {/* Footer */}
      <Box sx={{
        px: 2.5, py: 2,
        borderTop: '1px solid #E2E8F0',
        display: 'flex', gap: 1.5, flexShrink: 0, bgcolor: '#fff',
      }}>
        {step === 'select' && (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                flex: 1, fontWeight: 700, textTransform: 'none', borderRadius: '8px',
                color: '#64748B', borderColor: '#E2E8F0', '&:hover': { bgcolor: '#F8FAFC' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleNextReview}
              variant="contained"
              disableElevation
              disabled={selectedCount === 0 || windowExpired || isCreating}
              sx={{
                flex: 2, fontWeight: 800, textTransform: 'none', borderRadius: '8px',
                bgcolor: '#C2410C', '&:hover': { bgcolor: '#9A3412' },
                '&.Mui-disabled': { bgcolor: '#FED7AA', color: '#fff' },
              }}
            >
              {isCreating
                ? <><CircularProgress size={14} sx={{ color: '#fff', mr: 1 }} />Processing…</>
                : 'Next: Review →'}
            </Button>
          </>
        )}
        {step === 'review' && (
          <>
            <Button
              onClick={() => setStep('select')}
              variant="outlined"
              disabled={isCompleting}
              sx={{
                flex: 1, fontWeight: 700, textTransform: 'none', borderRadius: '8px',
                color: '#64748B', borderColor: '#E2E8F0', '&:hover': { bgcolor: '#F8FAFC' },
              }}
            >
              ← Back
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              disableElevation
              disabled={isCompleting}
              sx={{
                flex: 2, fontWeight: 800, textTransform: 'none', borderRadius: '8px',
                bgcolor: '#DC2626', '&:hover': { bgcolor: '#B91C1C' },
              }}
            >
              {isCompleting
                ? <><CircularProgress size={14} sx={{ color: '#fff', mr: 1 }} />Processing…</>
                : 'Confirm Return'}
            </Button>
          </>
        )}
        {step === 'success' && (
          <Button
            onClick={handleClose}
            variant="contained"
            disableElevation
            fullWidth
            sx={{
              fontWeight: 800, textTransform: 'none', borderRadius: '8px',
              bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' },
            }}
          >
            Done
          </Button>
        )}
      </Box>
    </Drawer>
  );
};

// ── Step 1: Select ────────────────────────────────────────────────────────────

function SelectStep({
  orderItems, windowExpired, daysRemaining, paidDate, returnWindowDays,
  cashierDecides, refundMethod, setRefundMethod, resolvedMethod,
  hasCustomer, notes, setNotes, reasonCodes, reasonRequired,
  selectedCount, getSelection, setQty, setReasonCode, itemErrors,
}) {
  return (
    <Stack spacing={2}>
      {/* Return window banner */}
      {returnWindowDays !== null && (
        <Box sx={{
          bgcolor: windowExpired ? '#FEF2F2' : '#FFF7ED',
          border: `1px solid ${windowExpired ? '#FECACA' : '#FED7AA'}`,
          borderRadius: '8px', px: 1.75, py: 1,
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <AccessTime sx={{ fontSize: 15, color: windowExpired ? '#EF4444' : '#C2410C', flexShrink: 0 }} />
          <Typography fontSize="0.78rem" fontWeight={600} color={windowExpired ? '#991B1B' : '#C2410C'}>
            {windowExpired
              ? `Return window expired (paid ${paidDate}, window was ${returnWindowDays} days)`
              : `Return window: ${daysRemaining} of ${returnWindowDays} days remaining (paid ${paidDate})`}
          </Typography>
        </Box>
      )}

      {/* Refund method */}
      {cashierDecides ? (
        <FormControl fullWidth size="small">
          <InputLabel>Refund Method</InputLabel>
          <Select
            value={refundMethod}
            onChange={(e) => setRefundMethod(e.target.value)}
            label="Refund Method"
            sx={{ borderRadius: '8px' }}
          >
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="store_credit" disabled={!hasCustomer}>
              <Box>
                Store Credit
                {!hasCustomer && (
                  <Typography component="span" fontSize="0.68rem" color="#94A3B8" sx={{ ml: 0.75 }}>
                    (requires customer)
                  </Typography>
                )}
              </Box>
            </MenuItem>
            <MenuItem value="same_payment_mode">Same Payment Mode</MenuItem>
          </Select>
        </FormControl>
      ) : (
        <Box sx={{
          bgcolor: '#F8FAFC', border: '1px solid #E2E8F0',
          borderRadius: '8px', px: 1.75, py: 1,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Typography fontSize="0.78rem" color="#64748B" sx={{ flexGrow: 1 }}>Refund method</Typography>
          <RefundMethodChip method={resolvedMethod} />
          <Typography fontSize="0.68rem" color="#94A3B8" fontStyle="italic">(set by org policy)</Typography>
        </Box>
      )}

      {/* Notes */}
      <TextField
        size="small"
        multiline
        maxRows={3}
        fullWidth
        placeholder="Add a note about this return… (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.82rem' } }}
      />

      {/* Item list header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
        <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem">
          SELECT ITEMS TO RETURN
        </Typography>
        {selectedCount > 0 && (
          <Typography fontSize="0.68rem" fontWeight={700} color="#64748B">
            {selectedCount} of {orderItems.length} selected
          </Typography>
        )}
      </Box>

      {/* Empty state: no items */}
      {orderItems.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <AssignmentReturn sx={{ fontSize: 44, color: '#CBD5E1', mb: 1 }} />
          <Typography color="#94A3B8" fontSize="0.85rem" fontWeight={600}>No items on this order</Typography>
        </Box>
      )}

      {/* Items */}
      <Stack spacing={1.25}>
        {orderItems.map((item) => {
          const sel = getSelection(item.id);
          const soldQty = parseFloat(item.quantity);
          const returnableQty = parseFloat(item.quantity_returnable ?? item.quantity);
          const fullyReturned = returnableQty <= 0;
          const err = itemErrors[item.id];
          const isSelected = sel.qty > 0;
          return (
            <Box
              key={item.id}
              sx={{
                border: `1px solid ${err ? '#FECACA' : fullyReturned ? '#E2E8F0' : isSelected ? '#FDBA74' : '#E2E8F0'}`,
                borderRadius: '10px',
                p: { xs: 1.5, sm: 1.75 },
                bgcolor: fullyReturned ? '#F8FAFC' : isSelected ? '#FFFAF5' : '#FFF',
                opacity: fullyReturned ? 0.65 : 1,
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              {/* Product info */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: isSelected ? 1.25 : 0 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: '8px', bgcolor: '#F1F5F9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <AssignmentReturn sx={{ fontSize: 14, color: '#94A3B8' }} />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} fontSize="0.85rem" color="#1E293B" noWrap>
                    {item.product_name}
                  </Typography>
                  <Typography fontSize="0.7rem" color="#94A3B8">
                    {item.product_sku && `${item.product_sku} · `}Sold: {soldQty}
                    {returnableQty < soldQty && ` · Returnable: ${returnableQty}`}
                  </Typography>
                  <ItemBarcode code={item.display_barcode} fontSize="0.7rem" />
                  {fullyReturned && (
                    <Typography fontSize="0.68rem" fontWeight={700} color="#16A34A" sx={{ mt: 0.25 }}>
                      Fully returned
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography fontWeight={700} fontSize="0.82rem" color="#1E293B">
                    ₹{parseFloat(item.line_total || 0).toFixed(2)}
                  </Typography>
                  {/* Qty stepper */}
                  {!fullyReturned && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.75, justifyContent: 'flex-end' }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center',
                        bgcolor: '#F8FAFC', border: '1px solid #E2E8F0',
                        borderRadius: '8px', overflow: 'hidden',
                      }}>
                        <IconButton
                          size="small"
                          onClick={() => setQty(item.id, sel.qty - 1)}
                          disabled={sel.qty <= 0 || windowExpired}
                          sx={{ borderRadius: 0, px: 0.75, color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}
                        >
                          <Remove sx={{ fontSize: 14 }} />
                        </IconButton>
                        <Typography sx={{ px: 1.25, fontWeight: 800, fontSize: '0.9rem', color: '#1E293B', minWidth: 28, textAlign: 'center' }}>
                          {sel.qty}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setQty(item.id, sel.qty + 1)}
                          disabled={sel.qty >= returnableQty || windowExpired}
                          sx={{ borderRadius: 0, px: 0.75, color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}
                        >
                          <Add sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                      <Typography fontSize="0.65rem" color="#94A3B8" sx={{ ml: 0.75 }}>
                        Max: {returnableQty}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Reason code — shown when item is selected and not fully returned */}
              {isSelected && !fullyReturned && reasonCodes.length > 0 && (
                <FormControl fullWidth size="small" error={!!err}>
                  <InputLabel>
                    {reasonRequired ? 'Reason *' : 'Reason (optional)'}
                  </InputLabel>
                  <Select
                    value={sel.reasonCodeId}
                    onChange={(e) => setReasonCode(item.id, e.target.value)}
                    label={reasonRequired ? 'Reason *' : 'Reason (optional)'}
                    sx={{ borderRadius: '8px', fontSize: '0.82rem' }}
                  >
                    {!reasonRequired && <MenuItem value=""><em>No reason</em></MenuItem>}
                    {reasonCodes.map((rc) => (
                      <MenuItem key={rc.id} value={rc.id}>{rc.name}</MenuItem>
                    ))}
                  </Select>
                  {err && <FormHelperText>{err}</FormHelperText>}
                </FormControl>
              )}

              {/* Per-item error banner */}
              {err && (
                <Box sx={{
                  mt: 1, bgcolor: '#FEF3C7', border: '1px solid #FDE68A',
                  borderRadius: '6px', px: 1.25, py: 0.75,
                  display: 'flex', alignItems: 'center', gap: 0.75,
                }}>
                  <Warning sx={{ fontSize: 13, color: '#F59E0B', flexShrink: 0 }} />
                  <Typography fontSize="0.72rem" color="#92400E">{err}</Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>

      {/* Window expired blocking */}
      {windowExpired && (
        <Box sx={{
          bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', p: 1.75,
          display: 'flex', alignItems: 'flex-start', gap: 1,
        }}>
          <Warning sx={{ fontSize: 16, color: '#EF4444', flexShrink: 0, mt: 0.1 }} />
          <Typography fontSize="0.8rem" color="#991B1B">
            The return window for this order has expired. No items can be returned.
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

// ── Step 2: Review ────────────────────────────────────────────────────────────

function ReviewStep({ draftReturn, resolvedMethod, notes }) {
  const items = draftReturn?.items || [];
  // monetary_refund_amount is the cash/credit actually handed back (gross refund
  // minus the loyalty-points slice, which is returned as points). Falls back to
  // refund_amount for non-loyalty returns / older API responses.
  const cashRefund = parseFloat(draftReturn?.monetary_refund_amount ?? draftReturn?.refund_amount ?? 0);
  const pointsRefunded = parseFloat(draftReturn?.loyalty_points_refunded ?? 0);
  const clawbackPromos = draftReturn?.promotion_clawback || [];
  const clawbackTotal = parseFloat(draftReturn?.promotion_clawback_total ?? 0);
  return (
    <Stack spacing={2}>
      {/* Meta card */}
      <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.75 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography fontSize="0.72rem" color="#64748B" fontWeight={600}>Refund Method</Typography>
          <RefundMethodChip method={resolvedMethod} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontSize="0.72rem" color="#64748B" fontWeight={600}>Return #</Typography>
          <Typography fontSize="0.78rem" fontWeight={700} color="#1E293B">{draftReturn.return_number}</Typography>
        </Box>
      </Box>

      {/* Items */}
      <Box>
        <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 1, px: 0.5 }}>
          ITEMS BEING RETURNED
        </Typography>
        <Stack spacing={1}>
          {items.map((item) => (
            <Box
              key={item.id}
              sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.75 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={700} fontSize="0.85rem" color="#1E293B" noWrap>
                    {item.product_name}
                  </Typography>
                  <Typography fontSize="0.72rem" color="#64748B">
                    ×{parseFloat(item.quantity_returned)} returned
                    {item.reason_name && ` · Reason: ${item.reason_name}`}
                  </Typography>
                  <ItemBarcode code={item.display_barcode} fontSize="0.7rem" />
                </Box>
                <Typography fontWeight={800} fontSize="0.92rem" color="#15803D" sx={{ flexShrink: 0 }}>
                  ₹{parseFloat(item.refund_amount).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Promotion clawback (only when this return breaks a promo whose
          discount sits on items the customer keeps — e.g. returning the "buy"
          item of a Buy X Get Y offer). The deduction is already reflected in
          monetary_refund_amount; this card explains it and gives the cashier
          the counter-offer to voice. */}
      {clawbackTotal > 0 && (
        <Box sx={{
          bgcolor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '10px',
          px: 2, py: 1.5,
        }}>
          <Typography fontWeight={800} fontSize="0.72rem" color="#C2410C" letterSpacing="0.04rem" sx={{ mb: 0.75 }}>
            PROMOTION REVERSED
          </Typography>
          <Stack spacing={0.5}>
            {clawbackPromos.map((p) => (
              <Box key={p.promotion_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography fontSize="0.78rem" color="#9A3412" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.promotion_name}
                </Typography>
                <Typography fontWeight={800} fontSize="0.82rem" color="#C2410C" sx={{ flexShrink: 0 }}>
                  −₹{parseFloat(p.gross).toFixed(2)}
                </Typography>
              </Box>
            ))}
          </Stack>
          <Typography fontSize="0.7rem" color="#9A3412" sx={{ mt: 1, lineHeight: 1.45 }}>
            The offer no longer applies to item(s) the customer is keeping, so its
            discount is deducted from this refund. Tip: returning the offer item(s)
            too gives the full refund.
          </Typography>
        </Box>
      )}

      {/* Points returned (only when the original bill used loyalty points) */}
      {pointsRefunded > 0 && (
        <Box sx={{
          bgcolor: '#FAF5FF', border: '1px solid #E9D5FF', borderRadius: '10px',
          px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <Typography fontWeight={800} fontSize="0.72rem" color="#7E22CE" letterSpacing="0.04rem">
            POINTS RETURNED
          </Typography>
          <Typography fontWeight={800} fontSize="0.92rem" color="#7E22CE">
            {pointsRefunded.toFixed(0)} pts
          </Typography>
        </Box>
      )}

      {/* Total cash/credit to hand back */}
      <Box sx={{
        bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px',
        px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Typography fontWeight={800} fontSize="0.75rem" color="#15803D" letterSpacing="0.06rem">
          {pointsRefunded > 0
            ? `${(REFUND_METHOD_LABELS[resolvedMethod] || 'Cash').toUpperCase()} REFUND`
            : 'TOTAL REFUND'}
        </Typography>
        <Typography fontWeight={900} fontSize="1.25rem" color="#15803D">
          ₹{cashRefund.toFixed(2)}
        </Typography>
      </Box>

      {/* Notes */}
      {notes && (
        <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', px: 1.75, py: 1.25 }}>
          <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.05rem" sx={{ mb: 0.5 }}>
            NOTES
          </Typography>
          <Typography fontSize="0.82rem" color="#374151" sx={{ whiteSpace: 'pre-wrap' }}>{notes}</Typography>
        </Box>
      )}

      {/* Warning */}
      <Box sx={{
        bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px',
        p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1,
      }}>
        <Warning sx={{ fontSize: 15, color: '#F59E0B', flexShrink: 0, mt: 0.1 }} />
        <Typography fontSize="0.75rem" color="#92400E">
          Confirming will apply the refund and restore inventory. This action cannot be undone.
        </Typography>
      </Box>
    </Stack>
  );
}

// ── Step 3: Success ───────────────────────────────────────────────────────────

function SuccessStep({ completedReturn, receipt, onPrint }) {
  const cashRefund = parseFloat(completedReturn?.monetary_refund_amount ?? completedReturn?.refund_amount ?? 0);
  const pointsRefunded = parseFloat(completedReturn?.loyalty_points_refunded ?? 0);
  return (
    <Stack spacing={2.5}>
      {/* Success icon */}
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CheckCircle sx={{ fontSize: 56, color: '#16A34A', mb: 1 }} />
        <Typography fontWeight={900} fontSize="1.1rem" color="#15803D">
          Return Processed!
        </Typography>
      </Box>

      {/* Details card */}
      <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', p: 2 }}>
        <Stack spacing={1.25}>
          <Box>
            <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.05rem" sx={{ mb: 0.25 }}>
              RETURN NUMBER
            </Typography>
            <Typography fontWeight={800} fontSize="0.92rem" color="#1E293B">
              {completedReturn.return_number}
            </Typography>
          </Box>
          {completedReturn.credit_note_number && (
            <>
              <Divider />
              <Box>
                <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.05rem" sx={{ mb: 0.25 }}>
                  CREDIT NOTE
                </Typography>
                <Typography fontWeight={800} fontSize="0.92rem" color="#1E293B">
                  {completedReturn.credit_note_number}
                </Typography>
              </Box>
            </>
          )}
          {completedReturn.debit_note_number && (
            <>
              <Divider />
              <Box>
                <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.05rem" sx={{ mb: 0.25 }}>
                  DEBIT NOTE (PROMO REVERSAL)
                </Typography>
                <Typography fontWeight={800} fontSize="0.92rem" color="#1E293B">
                  {completedReturn.debit_note_number}
                </Typography>
                <Typography fontSize="0.7rem" color="#C2410C" sx={{ mt: 0.25 }}>
                  Promotion discount of ₹{parseFloat(completedReturn.promotion_clawback_total ?? 0).toFixed(2)} reversed and deducted from refund
                </Typography>
              </Box>
            </>
          )}
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.05rem" sx={{ mb: 0.25 }}>
                {pointsRefunded > 0
                  ? `${(REFUND_METHOD_LABELS[completedReturn.refund_method] || 'Cash').toUpperCase()} REFUNDED`
                  : 'TOTAL REFUNDED'}
              </Typography>
              <Typography fontWeight={900} fontSize="1.05rem" color="#15803D">
                ₹{cashRefund.toFixed(2)}
              </Typography>
              {pointsRefunded > 0 && (
                <Typography fontSize="0.72rem" fontWeight={700} color="#7E22CE" sx={{ mt: 0.25 }}>
                  + {pointsRefunded.toFixed(0)} points returned
                </Typography>
              )}
            </Box>
            <RefundMethodChip method={completedReturn.refund_method} />
          </Box>
        </Stack>
      </Box>

      {/* Print return invoice */}
      <Button
        fullWidth
        variant="outlined"
        onClick={onPrint}
        startIcon={<Print />}
        sx={{
          fontWeight: 700, textTransform: 'none', borderRadius: '10px',
          color: '#0369A1', borderColor: '#BAE6FD', bgcolor: '#F0F9FF',
          '&:hover': { bgcolor: '#E0F2FE', borderColor: '#7DD3FC' },
        }}
      >
        Print Return Invoice
      </Button>

      {/* Send the credit note on WhatsApp, same as a bill at checkout */}
      <Box sx={{ mx: { xs: -1.5, sm: -2 } }}>
        <WhatsAppSection
          key={completedReturn.id}
          order={{ ...completedReturn, document_kind: 'credit_note' }}
          receipt={receipt}
          source="checkout"
          customerWhatsappOptIn={completedReturn.customer?.whatsapp_opt_in ?? null}
        />
      </Box>

    </Stack>
  );
}

export default ReturnItemsDrawer;
